// Shared small-widget helpers for the market panel's three tabs.
// Extracted so market-listings-tab.ts and market-list-form.ts (and
// future market-npc-tab.ts / market-history-tab.ts) can reuse one
// button factory + spinner pattern without each growing their own
// 40-line copy.
//
// All helpers are pure render functions: they construct and return
// PixiJS Containers wired with pointer handlers, and do not hold
// state across calls. Parents own lifecycle.

import { Container, Graphics, Text } from "pixi.js";
import { startHoldRepeat } from "../../shared/hold-repeat.ts";

export const SPIN_BTN_SZ = 22; // contracts:allow-literal — spinner button side
export const BTN_H = 22; // contracts:allow-literal — row-button height

const BTN_DISABLED = 0x1a1a2a;
const BTN_BORDER = 0x3355aa;
const BTN_BG = 0x1e3a6a;
const BTN_HOVER = 0x2e4a8a;
const BTN_TEXT = 0xeeeeff;
const TEXT_MUTED = 0x8888aa;
const BTN_SIZE = 11; // contracts:allow-literal — button label font size

export type ButtonPalette = { bg: number; hover: number; border: number };

export const BUTTON_PALETTE_DEFAULT: ButtonPalette = { bg: BTN_BG, hover: BTN_HOVER, border: BTN_BORDER };
export const BUTTON_PALETTE_BUY: ButtonPalette = { bg: 0x2a6a3a, hover: 0x3a8a4a, border: 0x44aa55 };
export const BUTTON_PALETTE_CANCEL: ButtonPalette = { bg: 0x6a3a2a, hover: 0x8a4a3a, border: 0xaa5544 };

// General-purpose row / action button. `disabled` dims + blocks
// interaction; `overrideLabel` lets the caller switch to the
// in-flight ("Buying…") state without changing the base label.
export function makeRowButton(opts: {
  label: string;
  palette: ButtonPalette;
  disabled: boolean;
  width: number;
  onClick: () => void;
  overrideLabel?: string | undefined;
}): Container {
  const c = new Container();
  const { label, palette, disabled, width, onClick, overrideLabel } = opts;
  if (!disabled) {
    c.eventMode = "static";
    c.cursor = "pointer";
  }
  const bg = new Graphics();
  const draw = (hover: boolean) => {
    bg.clear();
    bg
      .roundRect(0, 0, width, BTN_H, 3) // contracts:allow-literal — corner radius
      .fill({ color: disabled ? BTN_DISABLED : hover ? palette.hover : palette.bg })
      .stroke({ color: disabled ? TEXT_MUTED : palette.border, width: 1 }); // contracts:allow-literal
  };
  draw(false);
  c.addChild(bg);
  const t = new Text({
    text: overrideLabel ?? label,
    style: {
      fontSize: BTN_SIZE,
      fill: disabled ? TEXT_MUTED : BTN_TEXT,
      fontFamily: "monospace",
      fontWeight: disabled ? "normal" : "bold",
    },
  });
  t.x = Math.round((width - t.width) / 2); // contracts:allow-literal — centering idiom
  t.y = Math.round((BTN_H - t.height) / 2); // contracts:allow-literal — centering idiom
  c.addChild(t);
  if (!disabled) {
    c.on("pointerover", () => draw(true));
    c.on("pointerout", () => draw(false));
    c.on("pointertap", onClick);
  }
  return c;
}

// Small square "-"/"+" button for numeric spinners. Returns the
// right edge x-coordinate so the caller can lay out the next cell.
//
// Acceleration-on-hold: a quick tap (< 200 ms) increments by 1 once;
// holding past 200 ms fires onClick every 100 ms at step 1; past
// 1000 ms the step grows to 10. Lets users dial in listing prices
// and NPC quantities in the hundreds without 200 taps. Phase 4 close
// Fix 2.
const HOLD_REPEAT_DELAY_MS = 200; // contracts:allow-literal — UX pause before auto-repeat
const HOLD_REPEAT_INTERVAL_MS = 100; // contracts:allow-literal — auto-repeat cadence
const HOLD_BIG_STEP_AFTER_MS = 1000; // contracts:allow-literal — switch to big step after this long
const HOLD_BIG_STEP = 10; // contracts:allow-literal — big-step magnitude

export function renderSpinBtn(
  parent: Container,
  x: number,
  y: number,
  label: string,
  enabled: boolean,
  onClick: (step: number) => void,
): number {
  const c = new Container();
  c.x = x;
  c.y = y;
  if (enabled) {
    c.eventMode = "static";
    c.cursor = "pointer";
  }
  const bg = new Graphics();
  const draw = (hover: boolean) => {
    bg.clear();
    bg
      .roundRect(0, 0, SPIN_BTN_SZ, SPIN_BTN_SZ, 3) // contracts:allow-literal — corner radius
      .fill({ color: !enabled ? BTN_DISABLED : hover ? BTN_HOVER : BTN_BG })
      .stroke({ color: !enabled ? TEXT_MUTED : BTN_BORDER, width: 1 }); // contracts:allow-literal
  };
  draw(false);
  c.addChild(bg);
  const t = new Text({
    text: label,
    style: { fontSize: BTN_SIZE, fill: enabled ? BTN_TEXT : TEXT_MUTED, fontFamily: "monospace" },
  });
  t.x = Math.round((SPIN_BTN_SZ - t.width) / 2); // contracts:allow-literal
  t.y = Math.round((SPIN_BTN_SZ - t.height) / 2); // contracts:allow-literal
  c.addChild(t);
  if (enabled) {
    c.on("pointerover", () => draw(true));
    c.on("pointerout", () => draw(false));
    c.on("pointerdown", () => {
      // The pointerdown's first onClick(step=1) often triggers a tab
      // rebuild that destroys this very Container — Pixi pointerup
      // never reaches a destroyed Container, so we must clean up via a
      // window-level pointerup listener that survives the rebuild.
      // (Phase 7 Block A — playtest finding 9.3 spinner runaway.)
      const stop = startHoldRepeat({
        onTick: (step) => onClick(step),
        delayMs: HOLD_REPEAT_DELAY_MS,
        intervalMs: HOLD_REPEAT_INTERVAL_MS,
        bigStepAfterMs: HOLD_BIG_STEP_AFTER_MS,
        bigStep: HOLD_BIG_STEP,
        setTimeout: (cb, ms) => setTimeout(cb, ms),
        setInterval: (cb, ms) => setInterval(cb, ms),
        clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
        clearInterval: (h) => clearInterval(h as ReturnType<typeof setInterval>),
        now: () => Date.now(),
      });
      // Cast: server's tsconfig (no DOM lib) typechecks this file
      // transitively via test imports, but addEventListener is browser-
      // only at runtime — Pixi guarantees we're in the browser here.
      const w = globalThis as unknown as {
        addEventListener: (type: string, l: () => void) => void;
        removeEventListener: (type: string, l: () => void) => void;
      };
      const cleanup = (): void => {
        stop();
        w.removeEventListener("pointerup", cleanup);
        w.removeEventListener("pointercancel", cleanup);
      };
      w.addEventListener("pointerup", cleanup);
      w.addEventListener("pointercancel", cleanup);
    });
  }
  parent.addChild(c);
  return x + SPIN_BTN_SZ;
}

// Presentation truncation for player IDs displayed in listing rows.
// Phase 5+ can switch to a player-chosen display name column.
export function shortenId(id: string): string {
  return id.length <= 14 ? id : `${id.slice(0, 10)}…`; // contracts:allow-literal — presentation truncation
}
